import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /**
   * B2B / private cellar carts use the Storefront API (assets/b2b-storefront.js), not the
   * theme Ajax cart. We must not overwrite the bubble from theme cart:update events —
   * those often report 0 while the Storefront cart has items.
   *
   * Detection order: host attribute from Liquid (`data-b2b-storefront-cart`), wrapper link,
   * then bubble `data-b2b-cart-count` (see snippets/cart-bubble.liquid).
   */
  get #usesStorefrontCartBubble() {
    if (this.hasAttribute('data-b2b-storefront-cart')) return true;
    if (this.closest('[data-b2b-header-cart]')) return true;
    return !!(
      this.refs.cartBubbleCount?.hasAttribute('data-b2b-cart-count') ||
      this.querySelector('[data-b2b-cart-count]')
    );
  }

  /**
   * Fast path that does not depend on Component refs (may lag first paint). Used to skip
   * registering `cart:update` so theme code cannot call `renderCartBubble` for Storefront carts.
   */
  get #skipThemeCartUpdateListener() {
    return this.hasAttribute('data-b2b-storefront-cart') || !!this.closest('[data-b2b-header-cart]');
  }

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    if (!this.#skipThemeCartUpdateListener) {
      document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    }
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (!this.#skipThemeCartUpdateListener) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    }
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    if (this.#skipThemeCartUpdateListener || this.#usesStorefrontCartBubble) {
      if (typeof window !== 'undefined' && window.__B2B_CART_BUBBLE_DEBUG__) {
        console.info('[cart-icon] skip cart:update (Storefront bubble)', {
          itemCount: event.detail?.data?.itemCount ?? event.detail?.itemCount,
          sourceId: event.detail?.sourceId,
        });
      }
      return;
    }

    const data = event.detail?.data ?? event.detail ?? {};
    const raw = data.itemCount ?? 0;
    const itemCount = typeof raw === 'number' ? raw : Number(raw) || 0;
    const comingFromProductForm = data.source === 'product-form-component';

    if (typeof window !== 'undefined' && window.__B2B_CART_BUBBLE_DEBUG__) {
      console.info('[cart-icon] apply cart:update → renderCartBubble', {
        itemCount,
        comingFromProductForm,
        source: data.source,
        sourceId: event.detail?.sourceId,
      });
    }

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    if (this.#skipThemeCartUpdateListener || this.#usesStorefrontCartBubble) return;

    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.remove('visually-hidden');
    this.refs.cartBubble.classList.toggle('cart-bubble--zero', itemCount === 0);
    this.refs.cartBubble.setAttribute('aria-hidden', itemCount === 0 ? 'true' : 'false');
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', itemCount > 0 && animate);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    if (this.#skipThemeCartUpdateListener || this.#usesStorefrontCartBubble) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const visibleCount = this.refs.cartBubbleCount.textContent;

    if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
